import { z } from 'zod'
import { BotSessionState } from '../../enums/bot-session-state.enum'
import { BotEventType } from '../../enums/bot-event-type.enum'
import { orderResponseSchema } from './order-response.schema'

export const botSessionTickSchema = z.object({
  timestamp: z.coerce.date(),
  price: z.number(),
  state: z.nativeEnum(BotSessionState),
  peakPriceSeen: z.number(),
  armThreshold: z.number(),
  localLow: z.number().nullable(),
  confirmationTicks: z.number().int(),
  lastOrderPrice: z.number().nullable(),
  effectiveDropPercent: z.number(),
})

export type BotSessionTickDto = z.infer<typeof botSessionTickSchema>

export const botSessionEventSchema = z.object({
  id: z.string(),
  timestamp: z.coerce.date(),
  eventType: z.nativeEnum(BotEventType),
  metadata: z.record(z.string(), z.unknown()),
})

export type BotSessionEventDto = z.infer<typeof botSessionEventSchema>

export const botDebugResponseSchema = z.object({
  ticks: z.array(botSessionTickSchema),
  events: z.array(botSessionEventSchema),
})

export type BotDebugResponseDto = z.infer<typeof botDebugResponseSchema>

// max range = 31 days; exceeding this is almost always a client bug
// and produces unbounded responses
const MAX_DEBUG_WINDOW_MS = 31 * 24 * 60 * 60 * 1000

export const botDebugQuerySchema = z
  .object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.from && d.to && d.from.getTime() > d.to.getTime()) {
      ctx.addIssue({
        code: 'custom',
        message: '`from` must be <= `to`',
        path: ['from'],
      })
    }
    if (d.from && d.to && d.to.getTime() - d.from.getTime() > MAX_DEBUG_WINDOW_MS) {
      ctx.addIssue({
        code: 'custom',
        message: `range must be <= ${MAX_DEBUG_WINDOW_MS / (24 * 3_600_000)}d`,
        path: ['to'],
      })
    }
  })

export type BotDebugQueryDto = z.infer<typeof botDebugQuerySchema>

// a single FIFO buy lot a sell closed: how many lots it contributed and how long those shares were
// held (sell time − buy time, in ms). backs the per-row Held tooltip.
export const sellMatchedLotSchema = z.object({
  lots: z.number(),
  holdMs: z.number(),
})
export type SellMatchedLotDto = z.infer<typeof sellMatchedLotSchema>

// a bot-placed order with its live broker status, sourced from the reconciled TbankOrder
// table — one row per actual order whose `statusName`/`executionReportStatus` reflect the
// current lifecycle state (queued → filled/cancelled). extends the shared order DTO with the
// instrument display name pulled from the bot's tracked instruments, plus the per-sell realized
// P&L (FIFO-matched) and hold durations — all null for buys and sells that closed nothing.
export const botOrderEntrySchema = orderResponseSchema.extend({
  instrumentName: z.string().nullable(),
  // FIFO-matched realized gross (sell proceeds − cost of the closed lots); null unless a closing sell
  realizedGross: z.number().nullable(),
  // gross − pro-rated commission − per-sell tax; null unless a closing sell
  realizedNet: z.number().nullable(),
  // longest matched-lot hold duration in ms; null unless a closing sell
  heldMs: z.number().nullable(),
  // each consumed FIFO buy lot, longest-held first; null unless a closing sell
  matchedLots: z.array(sellMatchedLotSchema).nullable(),
})

export type BotOrderEntryDto = z.infer<typeof botOrderEntrySchema>

export const botLatestOrdersQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  // optional server-side filter: narrow the listing to a single instrument by its uid
  instrumentUid: z.string().min(1).optional(),
  // optional time window (in hours) for the first page: when set, the listing returns every
  // order newer than now − sinceHours instead of a fixed count. an empty window falls back to
  // the most recent `limit` orders. subsequent pages omit it and page by offset/limit.
  sinceHours: z.coerce.number().int().min(1).max(168).optional(),
})
export type BotLatestOrdersQueryDto = z.infer<typeof botLatestOrdersQuerySchema>

export const botLatestOrdersResponseSchema = z.object({
  entries: z.array(botOrderEntrySchema),
  hasMore: z.boolean(),
})
export type BotLatestOrdersResponseDto = z.infer<typeof botLatestOrdersResponseSchema>
